import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { BUSINESS } from "@/data";

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact & Free Quote | Cleaning Services Kitchener, Waterloo & Cambridge"
        description="Get a free, no-obligation cleaning quote in Kitchener, Waterloo or Cambridge. Call us today or send a request online. First-time customers save 20%."
        path="/contact"
      />
      <section className="mx-auto max-w-[1400px] px-6 pt-36 pb-16 lg:px-10 lg:pt-48">
        <p className="overline text-[var(--brand)]">Contact</p>
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.92] tracking-tighter sm:text-7xl lg:text-[6rem]">
          <MaskLines lines={["Let's get you", "sparkling."]} delay={0.1} lineClass="" />
        </h1>
        <Reveal delay={0.5} className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--ink-soft)]">
          Tell us about your space and we'll send a free, no-obligation quote within one business day. First-time
          customers get 20% off.
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-10 lg:pb-32">
        <div className="grid gap-16 lg:grid-cols-12">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <div className="border border-[var(--line)] p-8 lg:p-12">
              <h2 className="font-display text-3xl font-extrabold tracking-tight">Get your free quote</h2>
              <p className="mt-2 text-[var(--ink-soft)]">Fields marked are required. We'll never share your details.</p>
              <div className="mt-10">
                <QuoteForm />
              </div>
            </div>
          </Reveal>

          {/* Call us today */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline text-[var(--brand)]">Call us today</p>
              <a
                href={`tel:${BUSINESS.phoneHref}`}
                data-testid="contact-phone-big"
                className="mt-4 block font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-[var(--ink)] transition-colors hover:text-[var(--brand)] sm:text-5xl"
              >
                {BUSINESS.phoneDisplay}
              </a>
            </Reveal>

            <Reveal delay={0.1} className="mt-10 space-y-px bg-[var(--line)]">
              <a href={`tel:${BUSINESS.phoneHref}`} className="flex items-center gap-4 bg-white p-6 transition-colors hover:bg-[var(--brand-light)]">
                <Phone className="h-5 w-5 text-[var(--brand)]" />
                <div><p className="text-sm text-[var(--ink-soft)]">Phone</p><p className="font-semibold">{BUSINESS.phoneDisplay}</p></div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-4 bg-white p-6 transition-colors hover:bg-[var(--brand-light)]">
                <Mail className="h-5 w-5 text-[var(--brand)]" />
                <div><p className="text-sm text-[var(--ink-soft)]">Email</p><p className="font-semibold">{BUSINESS.email}</p></div>
              </a>
              <div className="flex items-center gap-4 bg-white p-6">
                <MapPin className="h-5 w-5 text-[var(--brand)]" />
                <div><p className="text-sm text-[var(--ink-soft)]">Service area</p><p className="font-semibold">{BUSINESS.areas.join(" · ")}</p></div>
              </div>
              <div className="flex items-center gap-4 bg-white p-6">
                <Clock className="h-5 w-5 text-[var(--brand)]" />
                <div><p className="text-sm text-[var(--ink-soft)]">Hours</p><p className="font-semibold">{BUSINESS.hours}</p></div>
              </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-8 bg-[var(--ink)] p-8 text-white">
              <p className="font-display text-2xl font-extrabold tracking-tight">First-time customer?</p>
              <p className="mt-2 text-white/60">Mention this page and save <span className="font-semibold text-[var(--mint)]">20%</span> on your first clean.</p>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
